import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Button, Layout, Menu, theme } from "antd";
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { RiDashboardFill } from "react-icons/ri";
import {
  AiOutlineUsergroupAdd,
  AiOutlineUpload,
} from "react-icons/ai";
import { FaUser, FaPhoneAlt } from "react-icons/fa";
import { BiSolidUserDetail } from "react-icons/bi";
import { IoNotifications } from "react-icons/io5";
import { GrStatusGood } from "react-icons/gr";
import { HiOutlineMegaphone } from "react-icons/hi2";
import { MdPlace, MdChecklist } from "react-icons/md";
import { TbReportSearch } from "react-icons/tb";
import scrollTop from "../helpers/scrollTop";
import SummaryApi from "../common";
import {
  getSessionUniversityId,
  setSessionUniversityId,
} from "../utils/sessionUser";
import logo from "../assest/sanjivani.png";
import { getEmployeeImageUrl } from "../utils/helpers";

const { Header, Content, Sider } = Layout;

/* ---------- layout constants ---------- */
const NAVY = "#172c57";
const ACCENT = "#2A4BCF";
const SIDER_WIDTH = 260;
const SIDER_COLLAPSED_WIDTH = 80;
const HEADER_HEIGHT = 64;

const Users = <AiOutlineUsergroupAdd />;

/* Items shared by "Digital Leads" and regular employees */
const studentStatusItems = [
  { key: "enrolled-students", icon: Users, label: "Total Enrolled Students" },
  // { key: "interested-students", icon: Users, label: "Interested Students" },
  // { key: "not-interested-students", icon: Users, label: "Not Interested Students" },
  // { key: "most-interested-students", icon: Users, label: "Most Interested Students" },
  { key: "callback-students", icon: Users, label: "Call Back Students" },
  // { key: "not-answered-students", icon: Users, label: "Not Answered Students" },
];

const AdminPanel = () => {
  const rawData = sessionStorage.getItem("user");
  const user = rawData ? JSON.parse(rawData) : {}
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = theme.useToken();

  const [memberType, setMemberType] = useState(null);
  const [employeeFullName, setEmployeeFullName] = useState("");
  const [collapsed, setCollapsed] = useState(false);
  const [openKeys, setOpenKeys] = useState([]);
  const [selectedUniversity, setSelectedUniversity] = useState(() => {
    return sessionStorage.getItem("selectedUniversityId") || undefined;
  });
  const [universityOptions, setUniversityOptions] = useState([]);

  const encodedEmployeeId = sessionStorage.getItem("H7Z4L9VD2XK5JW8QRYT6");
  const decodedEmployeeId = encodedEmployeeId ? atob(encodedEmployeeId) : "";

  /* ---------- member type + employee details ---------- */
  useEffect(() => {
    const fetchMemberType = () => {
      try {
        const encodedMemberType = sessionStorage.getItem("K3M8X1JQ5VZ4B7PWTL9");
        if (encodedMemberType) {
          setMemberType(atob(encodedMemberType));
        } else {
          navigate("/employee-login");
        }
      } catch (error) {
        toast.error(`Error: ${error.message}`);
        navigate("/employee-login");
      }
    };

    const fetchEmployeeDetails = async () => {
      try {
        const encodedId = sessionStorage.getItem("H7Z4L9VD2XK5JW8QRYT6");
        if (encodedId) {
          const response = await fetch(SummaryApi.getEmployeeById.url, {
            method: SummaryApi.getEmployeeById.method,
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer sandeep_university_916",
            },
            body: JSON.stringify({ employee_id: atob(encodedId) }),
          });

          if (!response.ok) {
            throw new Error("Failed to fetch employee data");
          }

          const employeeData = await response.json();
          setEmployeeFullName(employeeData?.data?.full_name);
        }
      } catch (error) {
        toast.error(`Error fetching employee details: ${error.message}`);
      }
    };

    fetchMemberType();
    fetchEmployeeDetails();
  }, [navigate]);

  /* ---------- universities ---------- */
  const fetchActiveUniversities = async () => {
    try {
      const response = await fetch(SummaryApi.getactiveuniversity.url, {
        method: SummaryApi.getactiveuniversity.method,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer sandeep_university_916",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch universities");
      }

      const result = await response.json();
      const rows = Array.isArray(result) ? result : result?.data || [];

      const options = rows
        .filter((u) => Number(u.is_active) === 1)
        .map((u) => ({
          value: String(u.university_id),
          label: u.university_name,
        }));

      setUniversityOptions(options);

      if (options.length > 0) {
        const savedUniversityId = sessionStorage.getItem("selectedUniversityId");
        const savedUniversityExists = options.some(
          (option) => String(option.value) === String(savedUniversityId),
        );
        setSelectedUniversity(
          savedUniversityExists ? savedUniversityId : options[0].value,
        );
      } else {
        setSelectedUniversity(undefined);
      }
    } catch (error) {
      // toast.error(`Error fetching universities: ${error.message}`);
    }
  };



  useEffect(() => {
    if (
      selectedUniversity &&
      getSessionUniversityId() !== String(selectedUniversity)
    ) {
      setSessionUniversityId(selectedUniversity);
    }
  }, [selectedUniversity]);

  /* ---------- actions ---------- */
  const handleLogout = async () => {
    try {
      if (memberType?.toLowerCase() === "employee" && decodedEmployeeId) {
        await fetch(SummaryApi.EmployeeAttendanceLogout.url, {
          method: SummaryApi.EmployeeAttendanceLogout.method,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer sandeep_university_916",
          },
          body: JSON.stringify({
            employee_id: decodedEmployeeId,
          }),
        });
      }
    } catch (error) {
      console.error("Attendance logout failed:", error);
    } finally {
      sessionStorage.removeItem("H7Z4L9VD2XK5JW8QRYT6");
      sessionStorage.removeItem("K3M8X1JQ5VZ4B7PWTL9");
      sessionStorage.removeItem("user");

      toast.success("Logout successfully");

      setTimeout(() => {
        navigate("/employee-login");
      }, 500);
    }
  };

  const handleRightClick = (e, url) => {
    e.preventDefault();
    window.open(url, "_blank");
  };

  const formatMenuItems = (items) =>
    items.map((item) => {
      if (item.children) {
        return {
          key: item.key,
          icon: item.icon,
          label: item.label,
          children: formatMenuItems(item.children),
        };
      }

      return {
        key: item.key,
        icon: item.icon,
        label: (
          <Link
            to={item.path || item.key}
            onClick={scrollTop}
            onContextMenu={(e) =>
              handleRightClick(e, `/${item.path || item.key}`)
            }
          >
            {item.label}
          </Link>
        ),
      };
    });

  const callWeeklyReportItem = {
    key: "employee-call-weekly-report",
    icon: <FaPhoneAlt />,
    label: "Employee Call Weekly Report",
  };

  const parentEmployeeChecklist = {
    key: "parent-employee-checklist",
    icon: <FaPhoneAlt />,
    label: "Parent & Employee Checklist",
  };

  const menuItems = (() => {
    const baseMenu = [
      { key: "", icon: <RiDashboardFill />, label: "Dashboard" },
    ];

    if (memberType === "Admin") {
      return [
        ...baseMenu,
        { key: "district", icon: <MdPlace />, label: "District" },
        { key: "city", icon: <MdPlace />, label: "City" },
        { key: "enrolled-students", icon: Users, label: "Total Enrolled Students" },
        { key: "interested-students", icon: Users, label: "Interested Students" },
        { key: "not-interested-students", icon: Users, label: "Not Interested Students" },
        { key: "most-interested-students", icon: Users, label: "Most Interested Students" },
        { key: "callback-students", icon: Users, label: "Call Back Students" },
        { key: "not-answered-students", icon: Users, label: "Not Answered Students" },
        { key: "excel-upload", icon: Users, label: "Student Excel Upload" },
        {
          key: "Reports",
          icon: <TbReportSearch />,
          label: "Reports",
          children: [
            {
              key: "All-Uploaded-Data",
              icon: <TbReportSearch />,
              label: "All Uploaded Data",
            },
            callWeeklyReportItem,
          ],
        },
        { key: "call-checklist", icon: <MdChecklist />, label: "Call Checklist" },
        { key: "all-employees", icon: Users, label: "All Employees" },
        { key: "all-teamLeads", icon: Users, label: "All Team Leads" },
        { key: "employee-task-report", icon: Users, label: "Employee Task Report" },
        { key: "teamlead-task-report", icon: Users, label: "Team Lead Task Report" },
        { key: "employee-calllogs", icon: <FaPhoneAlt />, label: "Employee Call Logs" },
        { key: "employee-call-duration", icon: <FaPhoneAlt />, label: "Employee Call Duration" },
        { key: "create-notification", icon: <IoNotifications />, label: "Create Notification" },
        { key: "closed-lead", icon: <GrStatusGood />, label: "Closed Lead" },
        { key: "search-students", icon: Users, label: "Search Students" },
        // { key: "digital-marketing-leads", icon: <HiOutlineMegaphone />, label: "DM Leads" },
        // parentEmployeeChecklist,
      ];
    }

    if (memberType === "Manager") {
      if (decodedEmployeeId === "34") {
        return [
          {
            key: "digital-marketing-leads",
            icon: <HiOutlineMegaphone />,
            label: "DM Leads",
          },
        ];
      }
      return [
        ...baseMenu,
        { key: "all-teamLeads", icon: Users, label: "All Team Leads" },
        { key: "enrolled-students", icon: Users, label: "Total Enrolled Students" },
        callWeeklyReportItem,
        { key: "employee-task-report", icon: Users, label: "Employee Task Report" },
      ];
    }

    if (memberType === "Team Lead") {
      return [
        ...baseMenu,
        { key: "my-employees", icon: Users, label: "My Team" },
        { key: "my-team-leads", icon: Users, label: "Today's Task" },
        { key: "tl-remainders", icon: Users, label: "All Reminders" },
        { key: "my-team-data", icon: Users, label: "All Leads" },
        // { key: "employee-task-report", icon: Users, label: "Employee Task Report" },

        // { key: "search-students", icon: Users, label: "Search Students" },
        callWeeklyReportItem,
        // {
        //   key: "student-excel-promotion-management",
        //   icon: <AiOutlineUpload />,
        //   label: "Promotion Management",
        //   children: [
        //     { key: "upload-excel-data", label: "Upload Excel Data" },
        //     { key: "promotion-excel-report", label: "Excel Report" },
        //   ],
        // },
      ];
    }

    if (memberType === "Digital Leads") {
      return [
        ...baseMenu,
        { key: "today-task", icon: <BiSolidUserDetail />, label: "Today's Task" },
        { key: "enroll-student", icon: <BiSolidUserDetail />, label: "Enroll Student" },
        ...studentStatusItems,
        { key: "search-students", icon: Users, label: "Search Students" },
        {
          key: "employee-promotion-excel-report",
          icon: <AiOutlineUpload />,
          label: "Promotion Excel Report",
        },
      ];
    }

    return [
      ...baseMenu,
      { key: "today-task", icon: <BiSolidUserDetail />, label: "Today's Task" },
      // { key: "enroll-student", icon: <BiSolidUserDetail />, label: "Enroll Student" },
      ...studentStatusItems,
      callWeeklyReportItem,
      // { key: "employee-team-lead", icon: Users, label: "Team Lead Report" },
      // { key: "search-students", icon: Users, label: "Search Students" },
      // {
      //   key: "employee-promotion-excel-report",
      //   icon: <AiOutlineUpload />,
      //   label: "Promotion Excel Report",
      // },
    ];
  })();

  const findActiveKeys = (items, pathname) => {
    let selected = "";
    let parent = "";

    const traverse = (list, parentKey = "") => {
      for (const item of list) {
        if (item.children) {
          traverse(item.children, item.key);
        }
        if (item.path && pathname === item.path) {
          selected = item.key;
          parent = parentKey;
        }
        if (!item.path && pathname === `/${item.key}`) {
          selected = item.key;
          parent = parentKey;
        }
      }
    };

    traverse(items);
    return { selected, parent };
  };

  const { selected, parent } = findActiveKeys(menuItems, location.pathname);

  useEffect(() => {
    if (parent) {
      setOpenKeys([parent]);
    }
  }, [parent]);

  const userInitial = (employeeFullName || "").trim().charAt(0).toUpperCase();

  return (
    <Layout hasSider style={{ minHeight: "100vh" }}>
      {/* ================= SIDEBAR ================= */}
      <Sider
        width={SIDER_WIDTH}
        collapsedWidth={SIDER_COLLAPSED_WIDTH}
        collapsed={collapsed}
        trigger={null}
        breakpoint="lg"
        onBreakpoint={(broken) => setCollapsed(broken)}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          bottom: 0,
          zIndex: 1001,
          background: NAVY,
          boxShadow: "2px 0 12px rgba(15, 23, 42, 0.18)",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
          {/* Logo + designation */}
          <div className={`sidebar-brand ${collapsed ? "is-collapsed" : ""}`}>
            <Link
              to="/"
              onClick={scrollTop}
              className="sidebar-brand-link"
              aria-label="Sanjivani University – Home"
            >
              <span className="sidebar-brand-tile">
                <img src={logo} alt="Sanjivani University" />
              </span>
            </Link>
            {!collapsed && memberType && (


              <span className="sidebar-brand-role">{memberType}</span>

            )}
          </div>

          <Menu
            theme="dark"
            mode="inline"
            className="admin-sidebar-menu"
            inlineCollapsed={collapsed}
            selectedKeys={[selected]}
            openKeys={collapsed ? [] : openKeys}
            onOpenChange={(keys) => setOpenKeys(keys)}
            onClick={({ key }) => {
              navigate(key);
              scrollTop();
            }}
            items={formatMenuItems(menuItems)}
          />
        </div>
      </Sider>

      {/* ================= MAIN AREA ================= */}
      <Layout
        style={{
          marginLeft: collapsed ? SIDER_COLLAPSED_WIDTH : SIDER_WIDTH,
          minHeight: "100vh",
          background: "#f1f4fa",
          transition: "margin-left 0.2s",
        }}
      >
        <Header
          style={{
            height: HEADER_HEIGHT,
            lineHeight: "normal",
            padding: "0 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
            background: "#ffffff",
            borderBottom: "1px solid #e3e8f2",
            boxShadow: "0 2px 8px rgba(15, 23, 42, 0.06)",
            position: "sticky",
            top: 0,
            zIndex: 1000,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            <Button
              type="text"
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ fontSize: 18, width: 40, height: 40, color: NAVY }}
            />
            <h1
              className="text-base font-bold sm:text-lg"
              style={{
                margin: 0,
                color: NAVY,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              SANJIVANI UNIVERSITY CRM
            </h1>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 14, flexShrink: 0 }}>
            <div className="header-user">
              {user?.employee_image ? (
                <img
                  src={getEmployeeImageUrl(user?.employee_image)}
                  alt={user?.full_name}

                  className="header-avatar object-contain"
                />
              ) : (
                <span className="header-avatar header-avatar-fallback">
                  {userInitial || <FaUser size={14} />}
                </span>
              )}
              <div className="header-user-text">
                <span className="header-user-name">{user?.full_name || employeeFullName}</span>
                <span className="header-user-role">{memberType}</span>
              </div>
            </div>

            <Button
              type="primary"
              danger
              icon={<LogoutOutlined />}
              onClick={handleLogout}
            >
              Logout
            </Button>
          </div>
        </Header>

        <Content
          style={{
            margin: 16,
            padding: 24,
            minHeight: 280,
            background: token.colorBgContainer,
            borderRadius: token.borderRadiusLG,
          }}
        >
          <Outlet />
        </Content>
      </Layout>

      <style>
        {`
  /* ---------- sidebar logo ---------- */
  .sidebar-brand {
    flex-shrink: 0;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    padding: 12px 16px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  }

  .sidebar-brand.is-collapsed {
    justify-content: center;
    padding: 12px 8px;
  }

  .sidebar-brand-link {
    display: flex;
    align-items: center;
    width: auto;
    flex-shrink: 0;
  }

  .sidebar-brand.is-collapsed .sidebar-brand-link {
    width: 100%;
    justify-content: center;
  }

  .sidebar-brand-tile {
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    background: transparent;
  }

  .sidebar-brand.is-collapsed .sidebar-brand-tile {
    justify-content: center;
  }

  .sidebar-brand-role {
    padding: 4px 12px;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.3;
    color: #ffffff;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    background: rgba(255, 255, 255, 0.12);
    border-radius: 999px;
  }

  .sidebar-brand-tile img {
    max-width: 140px;
    max-height: 88px;
    object-fit: contain;
  }

  .sidebar-brand.is-collapsed .sidebar-brand-tile img {
    max-width: 48px;
    max-height: 55px;
  }

  /* ---------- sidebar menu ---------- */
  .admin-sidebar-menu {
    flex: 1;
    min-height: 0;
    padding: 10px 0 20px;
    background: transparent !important;
    border-inline-end: 0 !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.4) transparent;
  }

  .admin-sidebar-menu::-webkit-scrollbar { width: 6px; }
  .admin-sidebar-menu::-webkit-scrollbar-track { background: transparent; }
  .admin-sidebar-menu::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.4);
    border-radius: 10px;
  }
  .admin-sidebar-menu::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.7);
  }

  .admin-sidebar-menu .ant-menu-sub,
  .admin-sidebar-menu .ant-menu-submenu > .ant-menu,
  .admin-sidebar-menu .ant-menu-item {
    background: transparent !important;
  }

  .admin-sidebar-menu .ant-menu-item a { color: inherit; }

  .admin-sidebar-menu .ant-menu-item-selected {
    background: #ffffff !important;
    color: ${ACCENT} !important;
    font-weight: 600;
    border-radius: 6px;
  }
  .admin-sidebar-menu .ant-menu-item-selected a,
  .admin-sidebar-menu .ant-menu-item-selected .anticon {
    color: ${ACCENT} !important;
  }

  /* ---------- header user ---------- */
  .header-user {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 4px 14px 4px 4px;
    background: #f4f6fb;
    border: 1px solid #e3e8f2;
    border-radius: 999px;
  }

  .header-avatar {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    object-fit: contain;
    flex-shrink: 0;
  }

  .header-avatar-fallback {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${NAVY};
    color: #ffffff;
    font-weight: 700;
    font-size: 14px;
  }

  .header-user-text {
    display: flex;
    flex-direction: column;
    line-height: 1.2;
  }

  .header-user-name {
    font-size: 14px;
    font-weight: 600;
    color: ${NAVY};
    text-transform: capitalize;
  }

  .header-user-role {
    font-size: 12px;
    color: #5b6b8c;
  }

  @media (max-width: 640px) {
    .header-user-text { display: none; }
    .header-user { padding: 4px; }
  }
`}
      </style>
    </Layout>
  );
};

export default AdminPanel;